import {
  RotateCcw,
  RotateCw,
  FlipHorizontal2,
  FlipVertical2,
  RefreshCcw,
} from 'lucide-react';
import { EditorSlider } from './EditorSlider';
import { ASPECT_OPTIONS, type CropState } from './imageEditorTypes';
import { useTranslation } from '../../i18n';
import styles from './ImageEditor.module.css';

interface CropControlsProps {
  cropState: CropState;
  onUpdate: (partial: Partial<CropState>) => void;
  onReset: () => void;
  onDragStart: () => void;
  onDragEnd: () => void;
}

export function CropControls({
  cropState,
  onUpdate,
  onReset,
  onDragStart,
  onDragEnd,
}: CropControlsProps) {
  const { t } = useTranslation();

  return (
    <div className={styles.tabContent}>
      {/* Aspect ratio pills */}
      <div className={styles.sectionLabel}>{t('imageEditor.aspectRatio')}</div>
      <div className={styles.pillRow}>
        {ASPECT_OPTIONS.map((opt) => {
          const label = 'labelKey' in opt ? t(opt.labelKey) : opt.label;
          return (
            <button
              key={label}
              type="button"
              className={`${styles.pill} ${cropState.aspect === opt.value ? styles.pillActive : ''}`}
              onClick={() => onUpdate({ aspect: opt.value })}
            >
              {label}
            </button>
          );
        })}
      </div>

      {/* Zoom */}
      <EditorSlider
        label={t('imageEditor.zoom')}
        min={1}
        max={3}
        step={0.05}
        value={cropState.zoom}
        defaultValue={1}
        formatValue={(v) => `${v.toFixed(1)}x`}
        onChange={(v) => onUpdate({ zoom: v })}
        onPointerDown={onDragStart}
        onPointerUp={() => onDragEnd()}
      />

      {/* Rotation */}
      <EditorSlider
        label={t('imageEditor.rotate')}
        min={0}
        max={360}
        step={1}
        value={cropState.rotation}
        defaultValue={0}
        formatValue={(v) => `${v}\u00B0`}
        onChange={(v) => onUpdate({ rotation: v })}
        onPointerDown={onDragStart}
        onPointerUp={() => onDragEnd()}
      />

      {/* Quick actions */}
      <div className={styles.sectionLabel}>{t('imageEditor.transform')}</div>
      <div className={styles.iconBtnRow}>
        <button
          type="button"
          className={styles.iconBtn}
          onClick={() => onUpdate({ rotation: (cropState.rotation - 90 + 360) % 360 })}
          title={t('imageEditor.rotateLeft')}
        >
          <RotateCcw size={16} />
        </button>
        <button
          type="button"
          className={styles.iconBtn}
          onClick={() => onUpdate({ rotation: (cropState.rotation + 90) % 360 })}
          title={t('imageEditor.rotateRight')}
        >
          <RotateCw size={16} />
        </button>
        <button
          type="button"
          className={`${styles.iconBtn} ${cropState.flipH ? styles.iconBtnActive : ''}`}
          onClick={() => onUpdate({ flipH: !cropState.flipH })}
          title={t('imageEditor.flipH')}
        >
          <FlipHorizontal2 size={16} />
        </button>
        <button
          type="button"
          className={`${styles.iconBtn} ${cropState.flipV ? styles.iconBtnActive : ''}`}
          onClick={() => onUpdate({ flipV: !cropState.flipV })}
          title={t('imageEditor.flipV')}
        >
          <FlipVertical2 size={16} />
        </button>
        <button
          type="button"
          className={styles.iconBtn}
          onClick={onReset}
          title={t('imageEditor.resetCrop')}
        >
          <RefreshCcw size={16} />
        </button>
      </div>
    </div>
  );
}
